import React, { useEffect, useMemo } from 'react';
import lodash from 'lodash';
import chroma from 'chroma-js';
import { connect } from 'react-redux';
import Loader from '../../../general/Loader';
import TeamPanel from './TeamPanel';
import SettingsForm from '../ui-kit/SettingsForm';
import { Row, Col, Card, ListGroup } from '../../../ui-kit';

import { selectors as combinedScheduleSelectors } from '../../../../state/modules/combined-schedules';
import { selectors as groupSelectors } from '../../../../state/modules/groups';
import { selectors as membershipSelectors } from '../../../../state/modules/memberships';
import {
  selectCurrentDashboardGroup,
  selectCurrentDashboardGroupOrSubgroups
} from '../../../../state/modules/dashboards/selectors';

const CrewAvailabilityWidgetSettings = ({ updateWidgetSettingsField, widgetSettings, dashboardGroupOrSubgroups }) => {
  useEffect(() => {
    // if the displayed_group_ids setting is not set, initialize it to include all groups
    if (widgetSettings.displayed_group_ids === undefined && dashboardGroupOrSubgroups !== undefined) {
      updateWidgetSettingsField(
        'displayed_group_ids',
        dashboardGroupOrSubgroups.map(group => group.id)
      );
    }
  }, [dashboardGroupOrSubgroups, widgetSettings.displayed_group_ids, updateWidgetSettingsField]);
  return (
    <SettingsForm
      withAutoScroll={true}
      withAutoResizeHeight={true}
      updateWidgetSettingsField={updateWidgetSettingsField}
      widgetSettings={widgetSettings}
    >
      <SettingsForm.Section>
        <SettingsForm.Section.Header>{I18n.t('display')}</SettingsForm.Section.Header>
        <SettingsForm.Section.Content>
          <SettingsForm.ToggleField
            fieldName="show_unavailable_teams"
            label={I18n.t('dashboards.widget_archetypes.available_crew.settings.show_unavailable')}
          />
          <SettingsForm.ToggleField
            fieldName="show_crew_in_unavailable_teams"
            label={I18n.t('dashboards.widget_archetypes.available_crew.settings.show_crew_in_unavailable_teams')}
            disabled={!widgetSettings.show_unavailable_teams}
          />
          <SettingsForm.ToggleField
            fieldName="show_tasks"
            label={I18n.t('dashboards.widget_archetypes.available_crew.settings.show_tasks')}
          />
        </SettingsForm.Section.Content>
      </SettingsForm.Section>
      <SettingsForm.Section>
        <SettingsForm.Section.Header>
          {I18n.t('dashboards.widget_archetypes.available_crew.settings.show_teams')}
        </SettingsForm.Section.Header>
        <SettingsForm.Section.Content>
          {dashboardGroupOrSubgroups.map(group => (
            <SettingsForm.ToggleField
              key={group.id}
              fieldName={`display_group_${group.id}`}
              value={(widgetSettings.displayed_group_ids || []).includes(group.id)}
              label={group.name}
              onChange={value => {
                updateWidgetSettingsField(
                  'displayed_group_ids',
                  value.target.checked
                    ? [...(widgetSettings.displayed_group_ids || []), group.id]
                    : (widgetSettings.displayed_group_ids || []).filter(id => id !== group.id)
                );
              }}
            />
          ))}
        </SettingsForm.Section.Content>
      </SettingsForm.Section>
    </SettingsForm>
  );
};

const settingsMapStateToProps = state => ({
  dashboardGroupOrSubgroups: selectCurrentDashboardGroupOrSubgroups(state)
});

const ConnectedCrewAvailabilityWidgetSettings = connect(settingsMapStateToProps)(CrewAvailabilityWidgetSettings);

const UnavailableTeamsPanelItem = ({ group }) => {
  const customColorPanelHeader = {
    backgroundColor: chroma(group.color || 'white')
      .alpha(0.15)
      .css()
  };
  return (
    <ListGroup.Item style={customColorPanelHeader}>
      <a href={`/groups/${group.id}/schedule/group_schedule`} style={{ color: 'var(--bs-body-color)' }}>
        {group.name}
      </a>
    </ListGroup.Item>
  );
};
const UnavailableTeamsPanel = ({ groups }) => {
  if (groups.length === 0) return null;
  return (
    <Col>
      <Card border="danger">
        <Card.Header className="opacity-75" style={{ backgroundColor: 'rgba(var(--bs-danger-rgb)' }}>
          <Card.Title>
            <h5>{I18n.t('dashboards.widget_archetypes.available_crew.unavailable_teams')}</h5>
          </Card.Title>
        </Card.Header>
        <ListGroup variant="flush">
          {groups.map(group => (
            <UnavailableTeamsPanelItem key={group.id} group={group} />
          ))}
        </ListGroup>
      </Card>
    </Col>
  );
};

const CrewAvailabilityWidget = ({
  availableMembers,
  dashboardAPIManager,
  groupsById,
  initialLoadComplete,
  dashboardGroup,
  dashboardGroupOrSubgroups,
  // if true, unavailable teams are displayed
  show_unavailable_teams,
  // if this and show_unavailable_teams is true, the team and its crew will be
  // displayed
  show_crew_in_unavailable_teams,
  show_tasks,
  taskAvailability,
  teamAvailability,
  displayed_group_ids,
  width
}) => {
  useEffect(() => {
    dashboardAPIManager.fetchUsers({ params: { presentation_type: 'basic' } });
    dashboardAPIManager.fetchSkills();
    dashboardAPIManager.fetchMemberships();
    dashboardAPIManager.fetchStandbyDutyFunctions();
    dashboardAPIManager.fetchAvailabilityCodes();
    dashboardAPIManager.registerForCombinedScheduleUpdates({ type: 'membership' });
    dashboardAPIManager.registerForCombinedScheduleUpdates({ type: 'team' });
    dashboardAPIManager.registerForCombinedScheduleUpdates({ type: 'task' });
  }, [dashboardAPIManager]);

  const parentGroup = useMemo(() => groupsById[dashboardGroup?.parent_group_id], [dashboardGroup, groupsById]);

  useEffect(() => {
    dashboardAPIManager.fetchGroups({ ids: [dashboardGroup?.parent_group_id], params: { in_hierarchy: true } });
  }, [dashboardAPIManager, dashboardGroup?.parent_group_id]);

  useEffect(() => {
    dashboardAPIManager.fetchGroups({ ids: displayed_group_ids || [], onlyMissing: true });
  }, [dashboardAPIManager, displayed_group_ids]);

  // show the name of the parent group if the displayed groups have distinct
  // ones
  const shouldShowParentGroup = useMemo(() => {
    if (dashboardGroupOrSubgroups === undefined) return false;

    const parents = dashboardGroupOrSubgroups.map(group => group.parent_group_id);
    const uniqueParents = lodash.uniq(parents);
    return uniqueParents.length > 1;
  }, [dashboardGroupOrSubgroups]);

  if (!initialLoadComplete) return <Loader />;
  if (dashboardGroupOrSubgroups === undefined) return <Loader />;

  // make a list of the groups and their availability. If configured, filter
  // out groups that are not currently available (see setting
  // show_unavailable_teams)
  const filteredGroups = dashboardGroupOrSubgroups
    .filter(group => (displayed_group_ids?.length ? displayed_group_ids.includes(group.id) : true))
    .map(group => {
      const { available } = teamAvailability[group.id] || {};

      if (!available) {
        // don't show anything if show_unavailable_teams is false
        if (!show_unavailable_teams) return;
        // don't show if show_unavailable_teams is true, but
        // show_unavailable_teams is false. In that case, the team is shown in
        // the UnavailableTeamsPanel
        if (show_unavailable_teams && !show_crew_in_unavailable_teams) return;
      }

      return { available, group };
    })
    .filter(Boolean);

  // these will be shown in a separate list of unavailable teams, but only if
  // show_unavailable_teams is true, but show_crew_in_unavailable_teams is not.
  // If show_crew_in_unavailable_teams is true, it will show the team as normal
  const unavailableGroups =
    show_unavailable_teams && !show_crew_in_unavailable_teams
      ? dashboardGroupOrSubgroups.filter(group => {
          const { available } = teamAvailability[group.id] || {};
          return !available;
        })
      : [];

  let maxCols = { 1: 1, 2: 2, 3: 4, 4: 5, 5: 6, 6: 8 }[width];
  maxCols = Math.min(filteredGroups.length, maxCols);
  if (unavailableGroups.length > 1) maxCols += 1;

  return (
    <Row xs={1} md={Math.min(maxCols, 3)} lg={Math.min(maxCols, 4)} xl={Math.min(maxCols, 6)} className="g-2">
      <>
        <UnavailableTeamsPanel groups={unavailableGroups} />
        {filteredGroups.map(({ group, available }) => (
          <TeamPanel
            key={group.id}
            team={group}
            parentGroup={parentGroup}
            available={available}
            showParentGroup={shouldShowParentGroup}
            availableMembers={availableMembers}
            taskAvailability={taskAvailability}
            groupCount={filteredGroups.length || 0}
            showTasks={show_tasks}
          />
        ))}
      </>
    </Row>
  );
};

Object.assign(CrewAvailabilityWidget, {
  SettingsContent: ConnectedCrewAvailabilityWidgetSettings,
  defaultSettings: {
    show_unavailable_teams: true,
    show_crew_in_unavailable_teams: true,
    show_tasks: true
  }
});

const mapStateToProps = state => ({
  groupsById: state.groups.byId,
  initialLoadComplete: groupSelectors.initialLoadComplete(state) && membershipSelectors.initialLoadComplete(state),
  availableMembers: combinedScheduleSelectors.availableMembersAtDashboardTime(state),
  teamAvailability: combinedScheduleSelectors.dashboardTime.team(state),
  taskAvailability: combinedScheduleSelectors.dashboardTime.task(state),
  dashboardGroup: selectCurrentDashboardGroup(state),
  dashboardGroupOrSubgroups: selectCurrentDashboardGroupOrSubgroups(state)
});

export default connect(mapStateToProps)(CrewAvailabilityWidget);
